<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-4">
        <div>
          <h1 class="sci-page-title-only">Abastecimentos Extras</h1>
          <p class="text-sm text-slate-500 font-medium mt-1">Visualize e gerencie abastecimentos fora da rotina padrão</p>
        </div>
      </div>

      <!-- FILTROS -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-filter-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                  FILTROS DE BUSCA
              </h2>
          </div>
          <div class="p-8">
              <v-row dense>
                  <v-col cols="12" sm="12" md="3">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Data Inicial</label>
                    <Date @dateValue="[(filter.startDate = $event)]" />
                  </v-col>
                  <v-col cols="12" sm="12" md="3">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Data Final</label>
                    <Date @dateValue="[(filter.endDate = $event)]" />
                  </v-col>
                  <v-col cols="12" sm="12" md="3">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Prefeitura</label>
                    <v-autocomplete
                      :disabled="userGroup == 1 ? false : true"
                      :items="prefectures"
                      v-model="filter.prefecture_id"
                      item-value="prefecture_id"
                      item-text="prefecture_social_name"
                      @change="[getVehicles(filter.prefecture_id, false, true), getDrivers(filter.prefecture_id), getProviders(filter.prefecture_id)]"
                      placeholder="Selecione a prefeitura"
                      outlined dense hide-details="auto"
                      class="bg-slate-50"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" sm="12" md="3">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Fornecedor</label>
                    <v-autocomplete
                      :items="providers"
                      item-value="provider_id"
                      item-text="provider_name"
                      v-model="filter.provider_id"
                      placeholder="Selecione o fornecedor"
                      outlined dense hide-details="auto"
                      class="bg-slate-50"
                      clearable
                    />
                  </v-col>
                   <!-- Removed Veículo and Motorista filters as requested -->
               </v-row>

               <div class="flex justify-end gap-3 mt-8">
                  <button 
                    @click="loadAbastecimento"
                    :disabled="!(filter.prefecture_id && filter.startDate && filter.endDate) || loader"
                    class="bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow-md shadow-orange-500/20 flex items-center justify-center gap-2 w-fit whitespace-nowrap px-8 h-10 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed text-[11px] font-bold uppercase tracking-widest"
                  >
                      <v-icon color="white" small>mdi-filter-variant</v-icon>
                      {{ loader ? 'FILTRANDO...' : 'FILTRAR RESULTADOS' }}
                  </button>
              </div>

          </div>
      </section>

      <!-- RESULTADOS -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
            <div class="flex items-center gap-3">
                <v-icon color="orange" small>mdi-format-list-bulleted</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                    LISTAGEM DE EXTRAS
                </h2>
            </div>
            <div class="flex items-center gap-1 w-full max-w-sm justify-end">
                <v-tooltip bottom color="slate-800">
                    <template v-slot:activator="{ on, attrs }">
                        <button 
                            v-bind="attrs" v-on="on" @click="loadAbastecimento"
                            class="p-2 rounded-lg text-slate-400 hover:bg-slate-600 hover:text-white transition-colors"
                        >
                            <v-icon color="currentColor">mdi-history</v-icon>
                        </button>
                    </template>
                    <span class="text-[10px] font-bold uppercase tracking-widest">Recarregar</span>
                </v-tooltip>
                
                <div class="h-6 border-r border-slate-500 mx-2"></div>

                <div class="relative w-full max-w-xs">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <v-icon small color="grey">mdi-magnify</v-icon>
                    </div>
                    <input 
                        v-model="search"
                        placeholder="PESQUISAR..."
                        class="w-full bg-white text-slate-800 text-[10px] font-bold uppercase tracking-widest rounded-lg py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all placeholder:text-slate-400 shadow-sm"
                    >
                </div>
            </div>
        </div>

        <v-data-table
          :headers="headers"
          :items="data"
          :search="search"
          :items-per-page="itemsPerPage"
          :loading="loader"
          loading-text="CARREGANDO..."
          class="elevation-0"
          hide-default-footer
          :page.sync="page"
          @page-count="pageCount = $event"
        >
          <template v-slot:item.datehour="{ item }">
            <span class="text-xs font-semibold text-slate-600">{{ convertDateTime(item.datehour) }}</span>
          </template>
          <template v-slot:item.supply_code="{ item }">
            <span class="text-xs font-black text-slate-700 tracking-widest">{{ item.supply_code }}</span>
          </template>

          <template v-slot:item.supply_liters_stocked="{ item }">

            <span class="font-bold text-slate-700">{{ realMaskII(Number(item.supply_liters_stocked || 0).toFixed(3)) }}</span>
          </template>
          <template v-slot:item.supply_authorized_value="{ item }">
            <span class="text-emerald-600 font-bold">R$ {{ realMask(Number(item.supply_authorized_value || 0).toFixed(2)) }}</span>
          </template>
          <template v-slot:item.supply_bidding_price="{ item }">
            <span class="text-slate-600 font-bold">R$ {{ realMask(Number(item.supply_bidding_price || 0).toFixed(2)) }}</span>
          </template>
          <template v-slot:item.supply_value="{ item }">
            <span class="font-black text-slate-800">R$ {{ realMask(Number(item.supply_value || (Number(item.supply_bidding_price) * Number(item.supply_liters_stocked)) || 0).toFixed(2)) }}</span>
          </template>
          <template v-slot:item.supply_authorized_liters="{ item }">
            <span class="font-bold text-slate-700">{{ realMaskII(Number(item.supply_authorized_liters || 0).toFixed(3)) }}</span>
          </template>
          <template v-slot:item.supply_status="{ item }">
            <span v-if="item.supply_confered" class="text-xs font-black text-emerald-600 uppercase">CONFERIDO</span>
            <span v-else class="text-xs font-semibold text-slate-600 uppercase">{{ item.supply_status }}</span>
          </template>
          <template v-slot:item.action="{ item }">
            <v-menu offset-y transition="scale-transition">
              <template v-slot:activator="{ on, attrs }">
                <button v-bind="attrs" v-on="on" class="p-1 rounded-md hover:bg-slate-100 transition-colors">
                  <v-icon small>mdi-dots-vertical</v-icon>
                </button>
              </template>

              <v-list dense class="py-1">
                <v-list-item @click="[(showHistoryModal = true), (historyItem = item)]" class="px-3 hover:bg-slate-50">
                  <v-icon small class="mr-2" color="indigo">mdi-text-box-search-outline</v-icon>
                  <v-list-item-title class="text-[11px] font-bold uppercase tracking-wider">Histórico e Justificativa</v-list-item-title>
                </v-list-item>

                <v-list-item @click="[(showPassword = true), (password = item.supply_code)]" :disabled="item.supply_status === 'CANCELADO'" class="px-3 hover:bg-slate-50">
                  <v-icon small class="mr-2" color="orange">mdi-key-outline</v-icon>
                  <v-list-item-title class="text-[11px] font-bold uppercase tracking-wider">Ver Senha</v-list-item-title>
                </v-list-item>

                <v-list-item @click="editar(item)" :disabled="item.supply_status === 'CANCELADO'" class="px-3 hover:bg-slate-50">
                  <v-icon small class="mr-2" color="blue">mdi-pencil-outline</v-icon>
                  <v-list-item-title class="text-[11px] font-bold uppercase tracking-wider">Editar</v-list-item-title>
                </v-list-item>

                <v-list-item @click="conferir(item)" :disabled="item.supply_status === 'CANCELADO' || item.supply_confered" class="px-3 hover:bg-slate-50">
                  <v-icon small class="mr-2" color="emerald">mdi-calendar-check-outline</v-icon>
                  <v-list-item-title class="text-[11px] font-bold uppercase tracking-wider text-emerald-600">{{ item.supply_confered ? 'Conferido' : 'Marcar como Conferido' }}</v-list-item-title>
                </v-list-item>

                <v-list-item @click="cancelar(item)" :disabled="userGroup != 1 || item.supply_status === 'CANCELADO'" class="px-3 hover:bg-slate-50">
                  <v-icon small class="mr-2" color="rose">mdi-delete-outline</v-icon>
                  <v-list-item-title class="text-[11px] font-bold uppercase tracking-wider text-rose-500">Excluir</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>

         <!-- Custom Pagination Footer -->
         <div class="bg-slate-50 px-6 py-4 border-t border-slate-200 flex flex-column md:flex-row justify-between items-center gap-4 text-xs text-slate-500 font-medium w-full">
            <span>Exibindo {{ paginationStart }} de {{ data.length }} registros</span>
            
            <div class="flex gap-1">
                <button 
                  @click="page > 1 ? page-- : null" 
                  :disabled="page === 1"
                  class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Anterior
                </button>
                
                <button 
                  v-for="p in visiblePages" 
                  :key="p"
                  @click="page = p"
                  :class="p === page ? 'bg-slate-700 text-white border-slate-700 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-100'"
                  class="px-3 py-1.5 border rounded-md transition-colors font-medium"
                >
                  {{ p }}
                </button>

                <button 
                  @click="page < pageCount ? page++ : null" 
                  :disabled="page === pageCount"
                  class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Próximo
                </button>
            </div>
         </div>
      </section>
    </div>

    <!-- MODAL EDITAR -->
    <v-dialog v-model="showModal" max-width="800px" persistent scrollable>
      <div class="bg-white rounded-xl overflow-hidden shadow-2xl font-sans flex flex-col max-h-[90vh]">
        <!-- Header -->
        <div class="px-6 py-4 flex justify-between items-center shrink-0" style="background-color: #333333;">
          <h3 class="text-white font-bold uppercase tracking-widest text-sm flex items-center gap-2">
            <v-icon color="orange" small>mdi-pencil</v-icon>
            {{ modalTitle }}
          </h3>
          <button @click="[showModal = false, clearForm()]" class="text-slate-400 hover:text-white transition-colors rounded-full p-1 hover:bg-slate-700">
            <v-icon small color="inherit">mdi-close</v-icon>
          </button>
        </div>

        <!-- Body -->
        <div class="p-6 overflow-y-auto">
          <v-form ref="validForm" v-if="!loadingModal" v-model="validForm" lazy-validation>
              <v-row dense>

                <v-col cols="12" md="6">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-1">Fornecedor</label>
                  <v-autocomplete
                    :rules="fieldRules"
                    :items="providers_form"
                    @change="loadItems(dataForm.provider_id, dataForm.vehicle_id, dataForm.prefecture_id)"
                    item-value="provider_id"
                    item-text="provider_name"
                    v-model="dataForm.provider_id"
                    placeholder="Selecione o fornecedor"
                    outlined dense hide-details="auto"
                    class="bg-slate-50"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-1">Item/Produto</label>
                  <v-autocomplete
                    :rules="fieldRules"
                    :items="items_form"
                    item-value="item_id"
                    item-text="item_description_desc"
                    v-model="dataForm.item_id"
                    placeholder="Selecione o item"
                    outlined dense hide-details="auto"
                    class="bg-slate-50"
                  >
                    <template slot="selection" slot-scope="{ item }">
                      <span v-if="item" class="text-sm font-medium">
                        {{ item.item_description_desc }} 
                        <span class="text-emerald-600 ml-2" v-if="item.item_price != null">
                          R$ {{ realMask(Number(item.item_price).toFixed(2)) }}
                        </span>
                      </span>
                    </template>
                    <template slot="item" slot-scope="{ item }">
                      <span v-if="item" class="text-sm">
                        {{ item.item_description_desc }} 
                        <span class="text-emerald-600 ml-2" v-if="item.item_price != null">
                          R$ {{ realMask(Number(item.item_price).toFixed(2)) }}
                        </span>
                      </span>
                    </template>
                  </v-autocomplete>
                </v-col>

                <v-col cols="12" md="6">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-1">Data e Hora</label>
                  <v-text-field
                    v-mask="'00/00/0000 00:00'"
                    v-model="dataForm.datehour"
                    outlined dense hide-details="auto"
                    class="bg-slate-50"
                    :rules="fieldRules"
                  />
                </v-col>
                
                <v-divider class="my-6 col-span-full"></v-divider>

                <v-col cols="12" md="6">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Litros Autorizados</label>
                  <v-text-field
                    v-model="dataForm.supply_authorized_liters"
                    v-mask-decimal.br="3"
                    :disabled="disableLitros"
                    outlined dense hide-details="auto"
                    class="bg-slate-50"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Valor Autorizado (R$)</label>
                  <v-text-field
                    v-model="dataForm.supply_authorized_value"
                    v-mask-decimal.br="2"
                    :disabled="disableValor"
                    outlined dense hide-details="auto"
                    class="bg-slate-50"
                  />
                </v-col>

                <template v-if="dataForm.supply_status === 'CONCLUIDO'">
                    <v-col cols="12" md="4">
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-1">Preço do Dia</label>
                      <v-text-field
                        v-model="dataForm.supply_price_day"
                        v-mask-decimal.br="3"
                        outlined dense hide-details="auto"
                        class="bg-slate-50 font-bold text-emerald-600"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-1">Preço Licitado</label>
                      <v-text-field
                        v-model="dataForm.supply_bidding_price"
                        v-mask-decimal.br="3"
                        outlined dense hide-details="auto"
                        class="bg-slate-50"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-1">Lts Abastecidos</label>
                      <v-text-field
                        v-model="dataForm.supply_liters_stocked"
                        v-mask-decimal.br="3"
                        outlined dense hide-details="auto"
                        class="bg-slate-50 font-bold"
                      />
                    </v-col>
                    <v-col cols="12">
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-1">Número do Cupom</label>
                      <v-text-field
                        v-model="dataForm.supply_coupon"
                        outlined dense hide-details="auto"
                        class="bg-slate-50"
                      />
                    </v-col>
                </template>

                <v-col cols="12">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-1">Justificativa</label>
                  <v-textarea
                    v-model="dataForm.justificativa"
                    rows="2"
                    outlined dense hide-details="auto"
                    class="bg-slate-50"
                    placeholder="Descreva o motivo deste abastecimento extra..."
                    :rules="fieldRules"
                    readonly
                  />
                </v-col>
              </v-row>
          </v-form>
          <div v-else class="flex flex-col items-center justify-center py-12">
            <v-progress-circular indeterminate color="orange" size="48"></v-progress-circular>
            <span class="mt-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Carregando dados...</span>
          </div>
        </div>

        <!-- Footer -->
        <div class="bg-slate-100 px-6 py-4 flex justify-end gap-3 border-t border-slate-200 shrink-0">
          <button 
            @click="[showModal = false, clearForm()]" 
            class="px-6 py-2 rounded-lg text-slate-500 font-bold uppercase tracking-widest text-[11px] hover:bg-slate-200 transition-colors"
          >
            CANCELAR
          </button>
          <button 
            @click="updateSupplyAuthorization(dataForm)" 
            :disabled="!validForm || loadingSubmit"
            class="px-8 h-10 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold uppercase tracking-widest text-[11px] shadow-md shadow-orange-500/20 transition-all transform hover:-translate-y-0.5 flex items-center gap-2"
          >
            <v-icon v-if="loadingSubmit" small class="mdi-spin" color="white">mdi-loading</v-icon>
            <v-icon v-else small color="white">mdi-check</v-icon>
            SALVAR ALTERAÇÕES
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- DIALOG HISTORICO E JUSTIFICATIVA -->
    <v-dialog v-model="showHistoryModal" max-width="600">
      <div class="bg-white rounded-xl overflow-hidden shadow-2xl font-sans" v-if="historyItem">
        <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
            <div class="flex items-center gap-3">
                <v-icon color="indigo" small dark>mdi-text-box-search-outline</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                    Detalhes do Abastecimento: {{ historyItem.supply_code }}
                </h2>
            </div>
            <button @click="showHistoryModal = false" class="text-slate-400 hover:text-white transition-colors rounded-full p-1 hover:bg-slate-700">
              <v-icon small color="inherit">mdi-close</v-icon>
            </button>
        </div>
        <div class="p-6">
            <h3 class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4 border-b border-slate-100 pb-2">Informações</h3>
            <div class="grid grid-cols-2 gap-4 mb-6">
                <div>
                    <div class="text-[10px] text-slate-500 font-bold uppercase mb-1">Data/Hora</div>
                    <div class="text-sm font-semibold text-slate-800">{{ convertDateTime(historyItem.datehour) }}</div>
                </div>
                <div>
                    <div class="text-[10px] text-slate-500 font-bold uppercase mb-1">Status</div>
                    <div class="text-sm font-black" :class="historyItem.supply_status === 'CONCLUIDO' ? 'text-emerald-600' : 'text-slate-800'">{{ historyItem.supply_status }}</div>
                </div>
                <div>
                    <div class="text-[10px] text-slate-500 font-bold uppercase mb-1">Volume (Lts)</div>
                    <div class="text-sm font-bold text-slate-800">{{ realMaskII(Number(historyItem.supply_liters_stocked || 0).toFixed(3)) }} L</div>
                </div>
                <div>
                    <div class="text-[10px] text-slate-500 font-bold uppercase mb-1">Valor Licitado</div>
                    <div class="text-sm font-bold text-slate-800">R$ {{ realMask(Number(historyItem.supply_bidding_price || 0).toFixed(2)) }}</div>
                </div>
                <div class="col-span-2">
                    <div class="text-[10px] text-slate-500 font-bold uppercase mb-1">Item / Combustível</div>
                    <div class="text-sm font-bold text-emerald-600 uppercase">{{ historyItem.item_description_desc || 'NÃO INFORMADO' }}</div>
                </div>
                <div class="col-span-2">
                    <div class="text-[10px] text-slate-500 font-bold uppercase mb-1">Valor Total</div>
                    <div class="text-sm font-black text-emerald-600">
                      R$ {{ realMask((Number(historyItem.supply_value || (Number(historyItem.supply_bidding_price) * Number(historyItem.supply_liters_stocked))) || 0).toFixed(2)) }}
                    </div>
                </div>
            </div>

            <h3 class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4 border-b border-slate-100 pb-2">Justificativa Registrada</h3>
            <div class="bg-indigo-50 border border-indigo-100 p-4 rounded-xl text-sm text-indigo-900 leading-relaxed font-medium">
                {{ historyItem.justificativa || 'Nenhuma justificativa fornecida para este abastecimento extra.' }}
            </div>
        </div>

        <div class="bg-slate-50 px-6 py-4 flex justify-end border-t border-slate-200">
            <button 
                @click="showHistoryModal = false"
                class="px-6 py-2 rounded-lg text-slate-500 font-bold uppercase tracking-widest text-[11px] hover:bg-slate-200 transition-colors"
            >
                FECHAR
            </button>
        </div>
      </div>
    </v-dialog>

    <!-- DIALOG SENHA -->
    <v-dialog v-model="showPassword" width="350">
      <div class="bg-white rounded-xl overflow-hidden shadow-2xl font-sans">
        <div class="px-6 py-4 flex flex-col items-center gap-2 pt-8">
            <div class="h-16 w-16 bg-orange-50 rounded-full flex items-center justify-center mb-2 text-orange-500">
                <v-icon size="32" color="inherit">mdi-lock-open-variant-outline</v-icon>
            </div>
            <h3 class="text-slate-800 font-black text-lg tracking-tight">Senha Extra</h3>
            <p class="text-xs text-slate-500 font-medium text-center">Utilize o código abaixo para validar o abastecimento no terminal.</p>
        </div>
        <div class="px-8 py-4 flex justify-center">
            <input 
                readonly 
                class="w-full text-center text-4xl font-black text-slate-800 tracking-[0.2em] bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl py-6 outline-none focus:border-orange-200 transition-colors"
                ref="copiar"
                :value="password"
            >
        </div>
        <div class="p-6 bg-slate-50 flex flex-col gap-2">
            <button 
                @click="copy"
                :class="[
                    'w-full h-11 rounded-lg font-bold uppercase tracking-widest text-[11px] transition-all flex items-center justify-center gap-2 shadow-sm',
                    copiado ? 'bg-emerald-500 text-white shadow-emerald-500/20' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                ]"
            >
                <v-icon small color="inherit">{{ copiado ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                {{ copiado ? 'COPIADO' : 'COPIAR CÓDIGO' }}
            </button>
            <button 
                @click="showPassword = false"
                class="w-full h-11 rounded-lg font-bold uppercase tracking-widest text-[11px] text-slate-400 hover:text-slate-600 transition-colors"
            >
                FECHAR
            </button>
        </div>
      </div>
    </v-dialog>

    <v-snackbar
      multi-line
      :color="snack.color"
      v-model="snack.show"
      transition="fade-transition"
      top right
    >
      <span class="text-[11px] font-bold uppercase tracking-widest">{{ snack.message }}</span>
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Date from "@/components/InputDate.vue";
import Helpers from "@/mixins/helpers.js";
export default {
  components: { Date },
  mixins: [Helpers],
  data() {
    return {
      userGroup: null,
      search: "",
      loader: false,
      showModal: false,
      modalTitle: "Editar Abastecimento Extra",
      showSearch: false,
      data: [],
      prefectures: [],
      providers: [],
      vehicles: [],
      drivers: [],
      filter: {
        prefecture_id: null,
        provider_id: null,
        vehicle_id: null,
        driver_id: null,
        all_status: true,
        supply_extra: true,
        allow_vehicle_null: true,
        allow_nfe: true,
        startDate: "",
        endDate: "",
      },
      snack: {
        show: false,
        message: "",
        color: "",
      },
      headers: [
        { text: "Data e Hora", value: "datehour", align: "center" },
        { text: "Senha", value: "supply_code", align: "center" },
        { text: "Fornecedor", value: "provider_name" },

        { text: "Item", value: "item_description_desc" },
        { text: "Lts. Aut.", value: "supply_authorized_liters", align: "center" },
        { text: "Vl. Aut.", value: "supply_authorized_value", align: "center" },
        { text: "Status", value: "supply_status", align: "center" },
        { text: "Lts. Abast.", value: "supply_liters_stocked", align: "center" },
        { text: "Vl. Licit.", value: "supply_bidding_price", align: "center" },
        { text: "Vl. Total", value: "supply_value", align: "center" },
        { text: "NF-e", value: "supply_coupon", align: "center" },
        { text: "Ações", align: "right", value: "action", sortable: false },
      ],
      loadingSubmit: false,
      loadingModal: false,
      validForm: false,
      providers_form: [],
      drivers_form: [],
      items_form: [],
      vehicles_form: [],
      fieldRules: [(v) => !!v || "Campo obrigatório"],
      dataForm: {
        supply_id: null,
        prefecture_id: null,
        vehicle_id: null,
        provider_id: null,
        driver_id: null,
        item_id: null,
        supply_authorized_liters: "",
        supply_authorized_value: "",
        supply_price_day: "",
        supply_bidding_price: "",
        supply_liters_stocked: "",
        supply_coupon: "",
        datehour: "",
        supply_status: "",
        justificativa: "",
      },
      password: "",
      showPassword: false,
      copiado: false,
      showHistoryModal: false,
      historyItem: null,
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
    };
  },
  created() {
    this.initialize();
  },
  computed: {
    disableLitros() {
      return parseFloat(this.dataForm.supply_authorized_value.replace(',', '.')) > 0;
    },
    disableValor() {
      return parseFloat(this.dataForm.supply_authorized_liters.replace(',', '.')) > 0;
    },
    paginationStart() {
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    visiblePages() {
      const pages = [];
      const totalVisible = 5;
      
      let start = Math.max(1, this.page - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCount, start + totalVisible - 1);

      if (end - start + 1 < totalVisible) {
        start = Math.max(1, end - totalVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  methods: {
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);

      this.filter.prefecture_id = this.$store.getters.getUserData.table_id;
      this.userGroup = this.$store.getters.getUserData.group_id;

      if (this.filter.prefecture_id) {
        this.getVehicles(this.filter.prefecture_id, false, true);
        this.getDrivers(this.filter.prefecture_id);
        this.getProviders(this.filter.prefecture_id);
      }


      this.$http.get("/prefectures").then((r) => {
        this.prefectures = r.data;
        this.loader = false;
        this.$store.dispatch("setLoader", this.loader);
      });
    },
    async getVehicles(prefecture_id, is_form = false, only_extra = false) {
      const res = await this.$http.get(`/vehicles/supplies/${prefecture_id}`);
      
      let dataFilter = res.data.filter(v => v.vehicle_active);
      if (only_extra) {
        dataFilter = dataFilter.filter(v => v.abastecimento_extra);
      }

      if (is_form) {
        this.vehicles_form = dataFilter;
      } else {
        this.vehicles = dataFilter;
      }
    },
    async getDrivers(prefecture_id, is_form = false) {
      const res = await this.$http.get(`/supplies/drivers/${prefecture_id}`);

      if (is_form) {
        this.drivers_form = res.data;
      } else {
        this.drivers = res.data;
      }
    },
    async getProviders(prefecture_id, is_form = false) {
      const res = await this.$http.get(`/providers/prefecture/${prefecture_id}`);
      if (is_form) {
        this.providers_form = res.data;
      } else {
        this.providers = res.data;
      }
    },
    loadAbastecimento() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .put("/supplies/billings", this.filter)
        .then((r) => {
          this.data = r.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.type = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    async loadItems(provider_id, vehicle_id, prefecture_id) {
      const hasProvider = provider_id !== null && provider_id !== undefined;
      const hasPrefecture = prefecture_id !== null && prefecture_id !== undefined;
      const hasVehicle = vehicle_id !== null && vehicle_id !== undefined && vehicle_id !== 'null' && vehicle_id !== '';

      if (hasProvider && hasPrefecture && !hasVehicle) {
        const res = await this.$http.get(`/supplies/providers/prefectures/${provider_id}/${prefecture_id}`);
        this.items_form = res.data;
        return;
      }

      if (!hasProvider || !hasPrefecture || !hasVehicle) {
        this.items_form = [];
        return;
      }

      const res = await this.$http.get(`/supplies/providers/${provider_id}/${vehicle_id}/${prefecture_id}`);
      this.items_form = res.data;
    },
    clearForm() {
      this.dataForm = {
        supply_id: null,
        prefecture_id: null,
        vehicle_id: null,
        provider_id: null,
        driver_id: null,
        item_id: null,
        supply_authorized_liters: "",
        supply_authorized_value: "",
        supply_price_day: "",
        supply_bidding_price: "",
        supply_liters_stocked: "",
        supply_coupon: "",
        datehour: "",
        supply_status: "",
        justificativa: "",
      };
      this.providers_form = [];
      this.items_form = [];
      this.drivers_form = [];
      this.vehicles_form = [];
      this.validForm = false;
    },
    async updateSupplyAuthorization(data) {
      if (this.$refs.validForm.validate()) {
        try {
          this.loadingSubmit = true;

          const payload = { ...data };
          delete payload.supply_id;
          delete payload.supply_status;
          delete payload.prefecture_id;

          payload.datehour = this.formatarStringData2(payload.datehour);
          payload.supply_authorized_liters = this.convertStringNumber(payload.supply_authorized_liters);
          payload.supply_authorized_value = this.convertStringNumber(payload.supply_authorized_value);
          
          if (data.supply_status === "CONCLUIDO") {
            payload.supply_price_day = this.convertStringNumber(payload.supply_price_day);
            payload.supply_bidding_price = this.convertStringNumber(payload.supply_bidding_price);
            payload.supply_liters_stocked = this.convertStringNumber(payload.supply_liters_stocked);

            payload.supply_value = this.round2(payload.supply_price_day * payload.supply_liters_stocked);
            payload.supply_bidding_value = this.round2(payload.supply_bidding_price * payload.supply_liters_stocked);
          } else {
            delete payload.supply_price_day;
            delete payload.supply_bidding_price;
            delete payload.supply_liters_stocked;
            delete payload.supply_coupon;
          }

          await this.$http.put(`/supplies/single/${data.supply_id}`, { update_simples: true, ...payload })
          this.snack.show = true;
          this.snack.message = "Autorização de abastecimento extra atualizada com sucesso!";
          this.snack.color = "success";
          this.loadingSubmit = false;
          this.showModal = false;
          this.clearForm();
          this.loadAbastecimento();
        } catch (error) {
          this.snack.show = true;
          this.snack.color = "error";
          this.snack.message = error.message;
          this.loadingSubmit = false;
        }
      }
    },
    async editar(item) {
      this.showModal = true;
      this.loadingModal = true;

      this.dataForm = {
        supply_id: item.supply_id,
        prefecture_id: item.prefecture_id,
        vehicle_id: item.vehicle_id,
        provider_id: item.provider_id,
        driver_id: item.driver_id,
        item_id: item.item_id,
        datehour: this.convertDateTime(item.datehour),
        supply_authorized_liters: this.convertDecimalNumber(Number(item.supply_authorized_liters || 0).toFixed(3)),
        supply_authorized_value: this.convertDecimalNumber(Number(item.supply_authorized_value || 0).toFixed(2)),
        supply_price_day: this.convertDecimalNumber(Number(item.supply_price_day || 0).toFixed(3)),
        supply_bidding_price: this.convertDecimalNumber(Number(item.supply_bidding_price || 0).toFixed(3)),
        supply_liters_stocked: this.convertDecimalNumber(Number(item.supply_liters_stocked || 0).toFixed(3)),
        supply_coupon: item.supply_coupon ? item.supply_coupon : "",
        supply_status: item.supply_status,
        justificativa: item.justificativa ? item.justificativa : "",
      };

      await this.getVehicles(item.prefecture_id, true, item.supply_extra);
      await this.getDrivers(item.prefecture_id, true);
      await this.getProviders(item.prefecture_id, true);
      await this.loadItems(item.provider_id, item.vehicle_id, item.prefecture_id);

      this.loadingModal = false;
    },
    async cancelar(item) {
      let msg = item.supply_status === 'BLOQUEADO PARA DISTRIBUIÇÃO' 
        ? "Este abastecimento ainda não foi processado pelo frentista. Deseja excluí-lo permanentemente?" 
        : "Deseja excluir o abastecimento?";
      
      let c = confirm(msg);
      if (c) {
        this.$store.dispatch("setLoader", true);
        
        if (item.supply_status === 'BLOQUEADO PARA DISTRIBUIÇÃO') {
          // Exclusão física para registros pendentes
          this.$http
            .delete(`/supplies/single/${item.supply_id}`)
            .then(() => {
              this.snack.show = true;
              this.snack.message = "Abastecimento excluído com sucesso!";
              this.snack.color = "success";
              this.loadAbastecimento();
            })
            .catch((e) => {
              this.snack.show = true;
              this.snack.message = e.message;
              this.snack.color = "error";
              this.$store.dispatch("setLoader", false);
            });
        } else {
          // Cancelamento lógico para registros já concluídos/processados
          this.$http
            .put(`/supplies/single/${item.supply_id}`, { update_simples: true, supply_status: "CANCELADO" })
            .then((r) => {
              this.snack.show = true;
              this.snack.message = r.data.result;
              this.snack.color = "success";
              this.loadAbastecimento();
            })
            .catch((e) => {
              this.snack.show = true;
              this.snack.message = e.message;
              this.snack.color = "error";
              this.$store.dispatch("setLoader", false);
            });
        }
      }
    },
    async conferir(item) {
      let c = confirm("Deseja marcar este abastecimento como conferido? Ele aparecerá na tela de conferência administrativa.");
      if (c) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .put(`/supplies/single/${item.supply_id}`, { update_simples: true, supply_confered: true, supply_status: "CONCLUIDO" })
          .then(() => {
            this.snack.show = true;
            this.snack.message = "Abastecimento marcado como conferido!";
            this.snack.color = "success";
            this.loadAbastecimento();
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    },
    copy() {
      var copyTextarea = this.$refs.copiar;
      copyTextarea.select();
      try {
        var successful = document.execCommand("copy");
        if (successful) {
          this.copiado = true;
          setTimeout(() => {
            this.showPassword = false;
            this.copiado = false;
          }, 1000);
        } else {
          alert("Opa, Não conseguimos copiar o texto, é possivel que o seu navegador não tenha suporte, tente usar Crtl+C.");
        }
      } catch (err) {
        alert("Opa, Não conseguimos copiar o texto, é possivel que o seu navegador não tenha suporte, tente usar Crtl+C.");
      }
    },
  },
};
</script>

<style scoped>
.title {
  text-transform: uppercase;
  font-weight: 400;
}
</style>
